import { Event } from '../model/event';
import { GeoCoordinates } from './geo-coordinates';
import { compareByDistanceFromUser } from './geo-distance';
import { SortCompare } from './editable-first';

/**
 * Returns a sortable date key 'YYYY-MM-DD' in local time,
 * or empty string if invalid or missing.
 */
export function getCalendarDateKey(dateTimeStr?: string | null): string {
  if (!dateTimeStr) {
    return '';
  }
  const date = new Date(dateTimeStr);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Compares two events:
 * 1. Start date (calendar day) ascending: earlier date first.
 * 2. If same date: distance from user ascending (closer first).
 * 3. If same date & distance: id descending (highest id first).
 */
export function compareEventsByStartDateAndDistance(
  user?: GeoCoordinates | null,
): SortCompare<Event> {
  return (first, second) => {
    const firstDateKey = getCalendarDateKey(first.startDate);
    const secondDateKey = getCalendarDateKey(second.startDate);

    if (!firstDateKey && !secondDateKey) {
      return (second.id ?? 0) - (first.id ?? 0);
    }
    if (!firstDateKey) {
      return 1;
    }
    if (!secondDateKey) {
      return -1;
    }

    // 1. Primary: Start date (day) ascending
    const dateDiff = firstDateKey.localeCompare(secondDateKey);
    if (dateDiff !== 0) {
      return dateDiff;
    }

    // 2. Same date: sort by distance from user if available
    if (user) {
      const distDiff = compareByDistanceFromUser(user, first.location, second.location);
      if (distDiff !== 0 && !Number.isNaN(distDiff)) {
        return distDiff;
      }
    }

    // 3. Tie-break by ID desc
    return (second.id ?? 0) - (first.id ?? 0);
  };
}
